import muxjs from "mux.js";

// --- Types ---

export type DownloadState =
  | "idle"
  | "fetching_manifest"
  | "downloading"
  | "paused"
  | "finalizing"
  | "complete"
  | "stopped"
  | "error";

export type CodecType = "h264" | "h265" | "vp9";

export interface DownloadProgress {
  state: DownloadState;
  message: string;
  currentSegment: number;
  totalSegments: number;
  bytesDownloaded: number;
  speed: number; // bytes per second
}

export interface DownloadCallbacks {
  onProgress: (progress: DownloadProgress) => void;
  onError: (error: Error) => void;
  onComplete: (savedFilename: string) => void;
}

interface M3U8Segment {
  url: string;
  duration: number;
}

// --- M3U8 Parser ---

const ALLOWED_ORIGINS = [
  "tvlabsassets.com",
  "tvlabsassets.dev",
  ".tvlabsassets.com",
  ".tvlabsassets.dev",
];

function validateOrigin(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return ALLOWED_ORIGINS.some((domain) => hostname === domain || hostname.endsWith(domain));
  } catch {
    return false;
  }
}

function resolveSegmentUrl(baseUrl: string, segment: string): string {
  if (segment.startsWith("http://") || segment.startsWith("https://")) {
    return segment;
  }
  const base = baseUrl.substring(0, baseUrl.lastIndexOf("/") + 1);
  return base + segment;
}

function parseM3U8(content: string, manifestUrl: string): M3U8Segment[] {
  const lines = content.split("\n").map((l) => l.trim());

  if (!lines[0]?.startsWith("#EXTM3U")) {
    throw new Error("Invalid M3U8: missing #EXTM3U header");
  }

  const segments: M3U8Segment[] = [];
  let currentDuration = 0;

  for (const line of lines) {
    if (line.startsWith("#EXTINF:")) {
      const match = line.match(/#EXTINF:([\d.]+)/);
      if (match) {
        currentDuration = parseFloat(match[1]);
      }
    } else if (line && !line.startsWith("#")) {
      const url = resolveSegmentUrl(manifestUrl, line);
      if (!validateOrigin(url)) {
        throw new Error(`Segment URL origin not allowed: ${new URL(url).hostname}`);
      }
      segments.push({ url, duration: currentDuration });
      currentDuration = 0;
    }
  }

  if (segments.length === 0) {
    throw new Error("Invalid M3U8: no segments found");
  }

  return segments;
}

// --- MP4 Init Segment Patching ---

// mux.js emits a fragmented MP4 init segment whose `mvhd`, `tkhd` and `mdhd`
// duration fields are all set to 0xFFFFFFFF (the "unknown" sentinel). Most
// players (ffprobe, browsers) ignore that and recompute the duration from
// samples, but VLC and some other players read the value literally and report
// the file as ~13h15m long (0xFFFFFFFF / timescale 90000). To get correct
// duration metadata we walk the moov box and overwrite the `mvhd` and `tkhd`
// durations with the total media duration computed from the HLS manifest.
// `mdhd` gets 0, since moov itself holds no samples: AVFoundation adds that
// value to the duration of the fragments, so QuickTime would show the
// recording twice as long.
//
// All three boxes are FullBoxes (1 byte version + 3 bytes flags) followed by
// version-dependent timing fields. We only touch the duration field and leave
// timescales / track ids / matrices untouched.
//
// Layout (relative to the start of the box, including the 8-byte header):
//   mvhd v=0: ts at 20, dur at 24 (u32). v=1: ts at 28, dur at 32 (u64).
//   mdhd v=0: ts at 20, dur at 24 (u32). v=1: ts at 28, dur at 32 (u64).
//   tkhd v=0: dur at 28 (u32).            v=1: dur at 36 (u64).
const UINT32_MAX = 0xffffffff;

function readU32(buf: Uint8Array, off: number): number {
  return ((buf[off] << 24) | (buf[off + 1] << 16) | (buf[off + 2] << 8) | buf[off + 3]) >>> 0;
}

function writeU32(buf: Uint8Array, off: number, value: number): void {
  buf[off] = (value >>> 24) & 0xff;
  buf[off + 1] = (value >>> 16) & 0xff;
  buf[off + 2] = (value >>> 8) & 0xff;
  buf[off + 3] = value & 0xff;
}

function writeU64(buf: Uint8Array, off: number, value: number): void {
  // Numbers up to 2^53 are exactly representable. Durations in any
  // timescale are far below that.
  const high = Math.floor(value / 0x100000000);
  const low = value >>> 0;
  writeU32(buf, off, high);
  writeU32(buf, off + 4, low);
}

function readBoxType(buf: Uint8Array, off: number): string {
  return String.fromCharCode(buf[off + 4], buf[off + 5], buf[off + 6], buf[off + 7]);
}

function toTicks(seconds: number, timescale: number): number {
  return Math.max(1, Math.round(seconds * timescale));
}

function writeDuration(
  buf: Uint8Array,
  boxOff: number,
  durationOffV0: number,
  ticks: number,
): void {
  const version = buf[boxOff + 8];
  if (version === 0) {
    writeU32(buf, boxOff + durationOffV0, Math.min(ticks, UINT32_MAX));
  } else {
    // v=1 has +8 bytes of additional 64-bit fields before the duration:
    // creation_time and modification_time bumped from u32 to u64 (=+8 bytes
    // total relative to the v=0 layout up through duration).
    writeU64(buf, boxOff + durationOffV0 + 8, ticks);
  }
}

export function patchInitSegmentDurations(initSegment: Uint8Array, durationSeconds: number): void {
  // Walk top-level boxes to find moov.
  let off = 0;
  while (off + 8 <= initSegment.byteLength) {
    const size = readU32(initSegment, off);
    if (size < 8 || off + size > initSegment.byteLength) return;
    const type = readBoxType(initSegment, off);
    if (type === "moov") {
      patchMoovDurations(initSegment, off + 8, off + size, durationSeconds);
      return;
    }
    off += size;
  }
}

function patchMoovDurations(
  buf: Uint8Array,
  moovStart: number,
  moovEnd: number,
  durationSeconds: number,
): void {
  // First pass: locate mvhd and read the movie timescale (needed for tkhd).
  let movieTimescale = 0;
  let scanOff = moovStart;
  while (scanOff + 8 <= moovEnd) {
    const size = readU32(buf, scanOff);
    if (size < 8 || scanOff + size > moovEnd) return;
    const type = readBoxType(buf, scanOff);
    if (type === "mvhd") {
      const version = buf[scanOff + 8];
      movieTimescale = readU32(buf, scanOff + (version === 0 ? 20 : 28));
      writeDuration(buf, scanOff, 24, toTicks(durationSeconds, movieTimescale));
      break;
    }
    scanOff += size;
  }
  if (movieTimescale === 0) return;

  // Second pass: patch each trak's tkhd and zero its mdhd.
  let off = moovStart;
  while (off + 8 <= moovEnd) {
    const size = readU32(buf, off);
    if (size < 8 || off + size > moovEnd) return;
    const type = readBoxType(buf, off);
    if (type === "trak") {
      patchTrakDurations(buf, off + 8, off + size, movieTimescale, durationSeconds);
    }
    off += size;
  }
}

function patchTrakDurations(
  buf: Uint8Array,
  trakStart: number,
  trakEnd: number,
  movieTimescale: number,
  durationSeconds: number,
): void {
  let off = trakStart;
  let tkhdOff = -1;
  while (off + 8 <= trakEnd) {
    const size = readU32(buf, off);
    if (size < 8 || off + size > trakEnd) return;
    const type = readBoxType(buf, off);
    if (type === "tkhd") {
      tkhdOff = off;
    } else if (type === "mdia") {
      // Find mdhd inside mdia.
      let inner = off + 8;
      const innerEnd = off + size;
      while (inner + 8 <= innerEnd) {
        const innerSize = readU32(buf, inner);
        if (innerSize < 8 || inner + innerSize > innerEnd) break;
        const innerType = readBoxType(buf, inner);
        if (innerType === "mdhd") {
          writeDuration(buf, inner, 24, 0);
          break;
        }
        inner += innerSize;
      }
    }
    off += size;
  }
  if (tkhdOff >= 0) {
    // tkhd.duration is in *movie* timescale, located at +28 (v=0) or +36
    // (v=1) — after creation_time, modification_time, track_id and a
    // reserved word.
    writeDuration(buf, tkhdOff, 28, toTicks(durationSeconds, movieTimescale));
  }
}

// --- Fragment Renumbering ---

// mux.js numbers each track's fragments separately, so the audio and video
// `moof` from one flush share an `mfhd` sequence number. AVFoundation needs the
// numbers to increase through the file and drops every fragment from the first
// repeat on, which leaves QuickTime with no video. Returns the number for the
// next fragment.
export function renumberFragments(data: Uint8Array, next: number): number {
  let off = 0;
  while (off + 8 <= data.byteLength) {
    const size = readU32(data, off);
    if (size < 8 || off + size > data.byteLength) break;
    if (readBoxType(data, off) === "moof" && readBoxType(data, off + 8) === "mfhd") {
      writeU32(data, off + 20, next++);
    }
    off += size;
  }
  return next;
}

// --- Transmuxer Adapters ---

interface TransmuxerAdapter {
  initialize(): void;
  push(data: Uint8Array): void;
  flush(): void;
  onInit(callback: (data: Uint8Array) => void): void;
  onData(callback: (data: Uint8Array) => void): void;
  onDone(callback: () => void): void;
  dispose(): void;
}

function createH264Transmuxer(): TransmuxerAdapter {
  const transmuxer = new muxjs.mp4.Transmuxer({
    remux: true,
    keepOriginalTimestamps: false,
  });

  let initCallback: ((data: Uint8Array) => void) | null = null;
  let dataCallback: ((data: Uint8Array) => void) | null = null;
  let doneCallback: (() => void) | null = null;
  let initSegmentEmitted = false;
  let nextSequenceNumber = 1;

  transmuxer.on("data", (segment: { initSegment: Uint8Array; data: Uint8Array }) => {
    if (!initSegmentEmitted) {
      // Emit the init segment (ftyp + moov) once at the start. The caller
      // is responsible for patching durations in the moov box before
      // writing it — mux.js leaves them at 0xFFFFFFFF and VLC reports the
      // file as ~13h15m without that fixup.
      initCallback?.(segment.initSegment);
      initSegmentEmitted = true;
    }
    nextSequenceNumber = renumberFragments(segment.data, nextSequenceNumber);
    dataCallback?.(segment.data);
  });

  transmuxer.on("done", () => {
    doneCallback?.();
  });

  return {
    initialize() {
      // mux.js transmuxer is ready after construction
    },
    push(data: Uint8Array) {
      transmuxer.push(data);
    },
    flush() {
      transmuxer.flush();
    },
    onInit(callback: (data: Uint8Array) => void) {
      initCallback = callback;
    },
    onData(callback: (data: Uint8Array) => void) {
      dataCallback = callback;
    },
    onDone(callback: () => void) {
      doneCallback = callback;
    },
    dispose() {
      transmuxer.dispose();
    },
  };
}

// Registry for codec adapters - add h265/vp9 here in the future
const TRANSMUXER_REGISTRY: Record<CodecType, () => TransmuxerAdapter> = {
  h264: createH264Transmuxer,
  h265: () => {
    throw new Error("H.265 transmuxing is not yet supported");
  },
  vp9: () => {
    throw new Error("VP9 transmuxing is not yet supported");
  },
};

// --- Constants ---

// Maximum memory allowed for in-memory blob fallback (512 MB)
const BLOB_FALLBACK_MAX_BYTES = 512 * 1024 * 1024;

// Timeout for fetching the manifest (30 seconds)
const MANIFEST_FETCH_TIMEOUT_MS = 30_000;

// Timeout for fetching each segment (60 seconds)
const SEGMENT_FETCH_TIMEOUT_MS = 60_000;

// --- Fetch Helpers ---

function fetchWithTimeout(url: string, signal: AbortSignal, timeoutMs: number): Promise<Response> {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const combinedSignal = AbortSignal.any([signal, timeoutSignal]);
  return fetch(url, { signal: combinedSignal });
}

// --- File Writer ---

interface FileWriter {
  readonly savedFilename: string;
  write(chunk: Uint8Array): Promise<void>;
  // Overwrite a previously-written prefix of the file. Used to rewrite the
  // moov box on finalize with the actual downloaded duration (which differs
  // from the planned duration when the user stops mid-download).
  rewritePrefix(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
}

async function createFileWriter(filename: string): Promise<FileWriter> {
  // Try File System Access API for streaming writes to disk
  if ("showSaveFilePicker" in window) {
    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: "MP4 Video",
            accept: { "video/mp4": [".mp4"] },
          },
        ],
      });
      const writable = await handle.createWritable({ keepExistingData: true });
      return {
        savedFilename: handle.name,
        async write(chunk: Uint8Array) {
          await writable.write(chunk);
        },
        async rewritePrefix(chunk: Uint8Array) {
          // Seek back to the start of the file and overwrite the prefix
          // with the new bytes. Subsequent writes resume from the seek
          // position, so we must seek back to the end before closing.
          await writable.seek(0);
          await writable.write(chunk);
        },
        async close() {
          await writable.close();
        },
      };
    } catch (e: any) {
      // User cancelled the picker or API not fully supported
      if (e.name === "AbortError") {
        throw e; // Re-throw user cancellation
      }
      // Fall through to blob fallback
    }
  }

  // Fallback: collect chunks in memory and download as blob
  const chunks: Uint8Array[] = [];
  let totalBytes = 0;
  return {
    savedFilename: filename,
    async write(chunk: Uint8Array) {
      totalBytes += chunk.byteLength;
      if (totalBytes > BLOB_FALLBACK_MAX_BYTES) {
        throw new Error(
          `Recording exceeds ${BLOB_FALLBACK_MAX_BYTES / (1024 * 1024)} MB. ` +
            `Use a browser that supports the File System Access API (Chrome or Edge) ` +
            `to download large recordings, or use the FFMPEG method.`,
        );
      }
      chunks.push(chunk);
    },
    async rewritePrefix(chunk: Uint8Array) {
      // For the in-memory fallback we know the prefix is exactly the first
      // chunk (the init segment), so swap it directly. Sizes match because
      // the patcher only mutates duration fields in place.
      if (chunks.length > 0 && chunks[0].byteLength === chunk.byteLength) {
        chunks[0] = chunk;
      }
    },
    async close() {
      const blob = new Blob(chunks as BlobPart[], { type: "video/mp4" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  };
}

// --- Main Downloader ---

const FIVE_SECONDS = 5 * 1000;

export class HLSDownloader {
  private state: DownloadState = "idle";
  private abortController: AbortController | null = null;
  private callbacks: DownloadCallbacks;
  private codec: CodecType;
  private bytesDownloaded = 0;
  private speedSamples: { time: number; bytes: number }[] = [];
  private pauseResolve: (() => void) | null = null;
  private stopRequested = false;
  private lastSegmentIndex = 0;
  private totalSegments = 0;

  constructor(callbacks: DownloadCallbacks, codec: CodecType = "h264") {
    this.callbacks = callbacks;
    this.codec = codec;
  }

  get isActive(): boolean {
    return (
      this.state === "fetching_manifest" ||
      this.state === "downloading" ||
      this.state === "paused" ||
      this.state === "finalizing"
    );
  }

  get isPaused(): boolean {
    return this.state === "paused";
  }

  async download(manifestUrl: string, filename: string): Promise<void> {
    if (this.isActive) return;

    this.state = "fetching_manifest";
    this.bytesDownloaded = 0;
    this.speedSamples = [];
    this.stopRequested = false;
    this.lastSegmentIndex = 0;
    this.totalSegments = 0;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    let transmuxer: TransmuxerAdapter | null = null;
    let writer: FileWriter | null = null;

    try {
      if (!validateOrigin(manifestUrl)) {
        throw new Error(`Manifest URL origin not allowed: ${new URL(manifestUrl).hostname}`);
      }

      this.emitProgress("Fetching manifest...", 0, 0);

      const response = await fetchWithTimeout(manifestUrl, signal, MANIFEST_FETCH_TIMEOUT_MS);
      if (!response.ok) {
        throw new Error(`Failed to fetch manifest: ${response.status}`);
      }
      const content = await response.text();
      const segments = parseM3U8(content, manifestUrl);
      this.totalSegments = segments.length;

      this.emitProgress("Preparing file...", 0, segments.length);
      writer = await createFileWriter(filename);

      transmuxer = TRANSMUXER_REGISTRY[this.codec]();
      transmuxer.initialize();

      // Capture the init segment so we can rewrite its moov box on finalize
      // with the actual downloaded duration. mux.js emits all duration
      // fields as 0xFFFFFFFF (the "unknown" sentinel); without rewriting,
      // VLC reports the file as ~13h15m. We initially patch with the full
      // manifest duration so an unfinalised file (e.g. browser closed
      // mid-download) still has a sane header.
      let initSegment: Uint8Array | null = null;
      const totalDuration = segments.reduce((acc, s) => acc + s.duration, 0);

      transmuxer.onInit(async (data: Uint8Array) => {
        initSegment = data;
        patchInitSegmentDurations(initSegment, totalDuration);
        await writer!.write(initSegment);
      });

      transmuxer.onData(async (data: Uint8Array) => {
        await writer!.write(data);
      });

      this.state = "downloading";

      let downloadedDuration = 0;

      for (let i = 0; i < segments.length; i++) {
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        if (this.stopRequested) break;

        // Wait if paused (state may be changed externally by pause())
        if ((this.state as DownloadState) === "paused") {
          await new Promise<void>((resolve) => {
            this.pauseResolve = resolve;
          });
          // After resume, check if stop was requested during pause
          if (this.stopRequested) break;
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        }

        this.emitProgress(
          `Downloading segment ${i + 1} of ${segments.length}`,
          i + 1,
          segments.length,
        );

        const segResponse = await fetchWithTimeout(
          segments[i].url,
          signal,
          SEGMENT_FETCH_TIMEOUT_MS,
        );
        if (!segResponse.ok) {
          throw new Error(`Failed to fetch segment ${i + 1}: ${segResponse.status}`);
        }

        const segData = new Uint8Array(await segResponse.arrayBuffer());
        this.trackBytes(segData.byteLength);

        transmuxer.push(segData);
        transmuxer.flush();
        this.lastSegmentIndex = i + 1;
        downloadedDuration += segments[i].duration;
      }

      // Finalize (both normal completion and stop)
      this.state = "finalizing";
      const downloaded = this.lastSegmentIndex;
      const message = this.stopRequested
        ? `Finalizing ${downloaded} of ${segments.length} segments...`
        : "Finalizing video...";
      this.emitProgress(message, downloaded, segments.length);

      transmuxer.dispose();
      transmuxer = null;

      // Rewrite the init segment with the actual downloaded duration. This
      // matters when the user stops mid-download: the planned duration we
      // initially baked in would otherwise overstate the recording length.
      if (initSegment && downloadedDuration > 0 && downloadedDuration < totalDuration) {
        const rewritten = new Uint8Array(initSegment);
        patchInitSegmentDurations(rewritten, downloadedDuration);
        await writer.rewritePrefix(rewritten);
      }

      const savedFilename = writer.savedFilename;
      await writer.close();
      writer = null;

      if (this.stopRequested) {
        this.state = "stopped";
        this.emitProgress(
          `Saved ${downloaded} of ${segments.length} segments`,
          downloaded,
          segments.length,
        );
      } else {
        this.state = "complete";
        this.emitProgress("Download complete!", segments.length, segments.length);
      }
      this.callbacks.onComplete(savedFilename);
    } catch (error: any) {
      if (error.name === "AbortError") {
        this.state = "idle";
        this.emitProgress("Download cancelled", 0, 0);
      } else if (error.name === "TimeoutError") {
        this.state = "error";
        this.callbacks.onError(
          new Error("Request timed out. Check your network connection and try again."),
        );
      } else {
        this.state = "error";
        this.callbacks.onError(error);
      }
    } finally {
      this.abortController = null;
      this.pauseResolve = null;
    }
  }

  pause(): void {
    if (this.state === "downloading") {
      this.state = "paused";
      this.emitProgress("Paused", this.lastSegmentIndex, this.totalSegments);
    }
  }

  resume(): void {
    if (this.state === "paused") {
      this.state = "downloading";
      if (this.pauseResolve) {
        this.pauseResolve();
        this.pauseResolve = null;
      }
    }
  }

  stop(): void {
    if (this.state === "downloading" || this.state === "paused") {
      this.stopRequested = true;
      // If paused, unblock the loop so it can break and finalize
      if (this.state === "paused" && this.pauseResolve) {
        this.pauseResolve();
        this.pauseResolve = null;
      }
    }
  }

  cancel(): void {
    this.abortController?.abort();
  }

  private trackBytes(bytes: number): void {
    this.bytesDownloaded += bytes;
    const now = performance.now();
    this.speedSamples.push({ time: now, bytes: this.bytesDownloaded });

    const cutoff = now - FIVE_SECONDS;
    this.speedSamples = this.speedSamples.filter((s) => s.time > cutoff);
  }

  private calculateSpeed(): number {
    if (this.speedSamples.length < 2) return 0;
    const first = this.speedSamples[0];
    const last = this.speedSamples[this.speedSamples.length - 1];
    const timeDiff = (last.time - first.time) / 1000;
    if (timeDiff === 0) return 0;
    return (last.bytes - first.bytes) / timeDiff;
  }

  private emitProgress(message: string, currentSegment: number, totalSegments: number): void {
    this.callbacks.onProgress({
      state: this.state,
      message,
      currentSegment,
      totalSegments,
      bytesDownloaded: this.bytesDownloaded,
      speed: this.calculateSpeed(),
    });
  }
}
